import {CATEGORIAS, digitsOnly, type VendorRegistration} from '@/lib/registration-schema';

/**
 * Traduce el formulario (camelCase, valores en español) al contrato de la API
 * de vendors (snake_case, enums en inglés). Es el único módulo que conoce el
 * vocabulario del backend — ver openapi.yaml, schema VendorApplication.
 */

type BusinessCategory =
  | 'food_beverage'
  | 'fashion'
  | 'beauty'
  | 'home'
  | 'services'
  | 'pets'
  | 'crafts'
  | 'health_wellness'
  | 'other';

type BoothNeed = 'table' | 'tablecloth' | 'chair' | 'none';

/** Tipado sobre CATEGORIAS: si alguien edita la lista, tsc falla aquí. */
const CATEGORY_MAP: Record<(typeof CATEGORIAS)[number], BusinessCategory> = {
  'Comida y bebida': 'food_beverage',
  Moda: 'fashion',
  Belleza: 'beauty',
  Hogar: 'home',
  Servicios: 'services',
  Mascotas: 'pets',
  Artesanías: 'crafts',
  'Salud y bienestar': 'health_wellness',
  Otro: 'other',
};

const BOOTH_MAP: Record<string, BoothNeed> = {
  mesa: 'table',
  mantel: 'tablecloth',
  silla: 'chair',
  ninguno: 'none',
};

/** Nombre de campo de la API → nombre en el formulario, para pintar errores 400. */
export const API_FIELD_TO_FORM: Record<string, keyof VendorRegistration> = {
  business_name: 'businessName',
  contact_name: 'contactName',
  position: 'role',
  email: 'email',
  phone: 'phone',
  municipality: 'municipio',
  business_category: 'category',
  category_other: 'categoryOther',
  registered_ath_business: 'hasAthBusiness',
  ath_business_name: 'pathName',
  previous_participation: 'participatedBefore',
  instagram: 'instagram',
  facebook: 'facebook',
  website: 'website',
  booth_needs: 'boothNeeds',
  products_services: 'products',
  is_evertec_merchant: 'isEvertecMerchant',
  merchant_id: 'merchantId',
  needs_electricity: 'needsElectricity',
  amperage: 'amperage',
  participation_confirmation: 'confirm',
};

export interface VendorApplicationPayload {
  business_name: string;
  contact_name: string;
  position: string;
  email: string;
  phone: string;
  municipality: string;
  business_category: BusinessCategory;
  category_other?: string;
  registered_ath_business: boolean;
  ath_business_name?: string;
  previous_participation: boolean;
  instagram?: string;
  facebook?: string;
  website?: string;
  booth_needs: BoothNeed[];
  products_services: string;
  is_evertec_merchant: boolean;
  merchant_id?: string;
  needs_electricity: boolean;
  amperage?: string;
  participation_confirmation: true;
}

/** Los campos opcionales vacíos se omiten: "" viola el minLength de la API. */
function clean(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** La API pide format: uri, pero el campo es texto libre ("tunegocio.com"). */
function normalizeUrl(value: string | undefined): string | undefined {
  const url = clean(value);
  if (!url) return undefined;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function toApiPayload(data: VendorRegistration): VendorApplicationPayload {
  // Sin campos de fecha: el backend genera received_at (decisión D1 del spec).
  const payload: VendorApplicationPayload = {
    business_name: data.businessName.trim(),
    contact_name: data.contactName.trim(),
    position: data.role.trim(),
    email: data.email.trim(),
    phone: digitsOnly(data.phone),
    municipality: data.municipio,
    business_category: CATEGORY_MAP[data.category],
    registered_ath_business: data.hasAthBusiness === 'si',
    previous_participation: data.participatedBefore === 'si',
    booth_needs: data.boothNeeds.map((id) => BOOTH_MAP[id]).filter((need): need is BoothNeed => need !== undefined),
    products_services: data.products.trim(),
    is_evertec_merchant: data.isEvertecMerchant !== 'ninguno',
    needs_electricity: data.needsElectricity === 'si',
    participation_confirmation: true,
  };

  const optional: Partial<VendorApplicationPayload> = {
    category_other: data.category === 'Otro' ? clean(data.categoryOther) : undefined,
    ath_business_name: payload.registered_ath_business ? clean(data.pathName) : undefined,
    instagram: clean(data.instagram),
    facebook: clean(data.facebook),
    website: normalizeUrl(data.website),
    // La API exige merchant_id siempre que is_evertec_merchant = true, y no
    // tiene un campo propio para el servicio: "POS" viaja como valor fijo y
    // "otro" lleva el texto que escribió el comercio.
    merchant_id:
      data.isEvertecMerchant === 'pos'
        ? 'POS de Evertec'
        : data.isEvertecMerchant === 'otro'
          ? clean(data.merchantId)
          : undefined,
    // Solo viaja si hace falta: el contrato lo exige cuando
    // needs_electricity = true, y lo ignora cuando es false.
    amperage: payload.needs_electricity ? clean(data.amperage) : undefined,
  };

  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) Object.assign(payload, {[key]: value});
  }

  return payload;
}
